import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BRAND } from "@/lib/api";
import PrintHeader from "@/components/PrintHeader";
import LetterheadToggle, { useLetterheadMode } from "@/components/LetterheadToggle";

export default function PrintInvoice() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);
  const [admission, setAdmission] = useState(null);
  const letterhead = useLetterheadMode();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/billing/invoices/${id}`);
      setInv(data);
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      if (data.admission_id) {
        try {
          const { data: ad } = await api.get(`/ipd/admissions/${data.admission_id}`);
          setAdmission(ad.admission);
        } catch { /* admission may have been deleted */ }
      }
      setTimeout(() => window.print(), 700);
    })();
  }, [id]);

  if (!inv) return <div className="p-10 text-sm text-ink-muted">Loading…</div>;

  // Prefer admission snapshot for panel info (canonical), fall back to invoice fields
  const panelName  = (admission?.panel_name) || inv.panel_name || "";
  const policyNo   = (admission?.policy_no)  || inv.panel_card_no || "";
  const tpaName    = admission?.tpa_name || "";
  const corporateName = admission?.corporate_name || "";
  const payerCat   = (admission?.payer_category) || "cash";

  const admitDate     = (admission?.admitted_at || "").slice(0, 16).replace("T", " ");
  const dischargeDate = (admission?.discharged_at || "").slice(0, 16).replace("T", " ");
  const ward          = admission?.ward || "";
  const bedNo         = admission?.bed_no || "";
  const ipdNo         = admission?.ipd_no || "";

  // LOS (days)
  let los = "";
  if (admission?.admitted_at) {
    const a = new Date(admission.admitted_at);
    const d = admission?.discharged_at ? new Date(admission.discharged_at) : new Date();
    const diff = Math.max(1, Math.ceil((d - a) / (1000 * 60 * 60 * 24)));
    los = `${diff} day${diff > 1 ? "s" : ""}`;
  }

  // Single Patient Detail block fields — panel info merged in here as the user requested
  const detailFields = [
    ["Patient",         inv.patient_name,           "text-sm"],
    ["UHID",            inv.patient_uhid,           "text-sm font-mono"],
    admission && ["IPD No.",         ipdNo,                      "text-sm font-mono"],
    admission && ["Admission date",  admitDate || "—",           "text-sm"],
    admission && ["Discharge date",  dischargeDate || "—",       "text-sm"],
    admission && ["Ward / Bed",      `${ward || "—"}${bedNo ? ` · ${bedNo}` : ""}`, "text-sm"],
    admission && ["LOS",             los || "—",                 "text-sm"],
    admission && ["Admission type",  (admission.admission_type || "").replace("_", " "), "text-sm capitalize"],
    ["Payment mode",    (inv.payment_mode || "").replace("_", " "), "text-sm capitalize"],
    (payerCat !== "cash" || panelName) && ["Payer", payerCat,    "text-sm capitalize"],
    panelName       && ["Panel / Company", panelName,            "text-sm"],
    policyNo        && ["Policy / Card no.", policyNo,           "text-sm font-mono"],
    tpaName         && ["TPA",               tpaName,            "text-sm"],
    corporateName   && ["Corporate",         corporateName,      "text-sm"],
  ].filter(Boolean);

  return (
    <div className="mx-auto max-w-3xl bg-white p-10 text-sm text-ink" data-testid="print-invoice">
      {/* Header */}
      <LetterheadToggle />
      {!letterhead && (
        <PrintHeader
          hospital={hospital}
          branch={branch}
          subtitle={`${inv.bill_state === "final" ? "Tax Invoice" : (inv.receipt_type ? inv.receipt_type + " Receipt" : "Receipt")}`}
          right={<>
            <div>{inv.bill_state === "final" ? "Invoice" : "Receipt"} No: <span className="font-mono font-semibold" data-testid="print-invoice-no">{inv.invoice_no}</span></div>
            <div>Date: {(inv.created_at || "").slice(0, 10)}</div>
            {inv.receipt_type && <div className="mt-1 inline-block rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-brand">{inv.receipt_type}</div>}
            {inv.bill_state === "final" && <div className="mt-1 ml-1 inline-block rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">Final</div>}
          </>}
        />
      )}

      {/* === Unified Patient Detail block (Patient + Admission + Panel/Insurance) === */}
      <div className="mt-3 rounded border border-border bg-secondary/30 p-3">
        <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand">Patient detail</div>
        <div className="grid grid-cols-4 gap-x-3 gap-y-2 text-[11px]">
          {detailFields.map(([label, value, valueClass]) => (
            <div key={label}>
              <div className="font-semibold uppercase text-[9px] tracking-wider text-ink-muted">{label}</div>
              <div className={valueClass}>{value || "—"}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Items table — Item code now a dedicated bold column */}
      <table className="mt-3 w-full border border-border text-[11px]">
        <thead className="bg-secondary">
          <tr>
            <th className="px-2 py-1 text-left w-24">Item code</th>
            <th className="px-2 py-1 text-left">Description</th>
            <th className="px-2 py-1 text-left w-20">Date</th>
            <th className="px-2 py-1 text-right w-12">Qty</th>
            <th className="px-2 py-1 text-right w-20">Rate</th>
            <th className="px-2 py-1 text-right w-14">GST%</th>
            <th className="px-2 py-1 text-right w-24">Total</th>
          </tr>
        </thead>
        <tbody>
          {inv.items.map((it, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-2 py-1 font-mono font-semibold">{it.item_code || "—"}</td>
              <td className="px-2 py-1">{it.description}</td>
              <td className="px-2 py-1">{(it.date || "").slice(0, 10)}</td>
              <td className="px-2 py-1 text-right">{it.qty}</td>
              <td className="px-2 py-1 text-right">₹ {(it.rate || 0).toFixed(2)}</td>
              <td className="px-2 py-1 text-right">{it.gst_percent || 0}%</td>
              <td className="px-2 py-1 text-right">₹ {(it.line_total || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Totals + advances */}
      <div className="mt-3 grid grid-cols-2 gap-4">
        <div className="text-[11px]">
          {(inv.advances || []).length > 0 && (
            <>
              <div className="mb-1 font-semibold uppercase text-ink-muted">Advances received</div>
              <table className="w-full border border-border">
                <thead className="bg-secondary text-[10px]">
                  <tr>
                    <th className="px-2 py-1 text-left">Receipt no.</th>
                    <th className="px-2 py-1 text-left">Date</th>
                    <th className="px-2 py-1 text-left">Mode</th>
                    <th className="px-2 py-1 text-left">Ref</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.advances.map((a, i) => {
                    const rcptNo = a.receipt_no || `#${i + 1}`;
                    return (
                      <tr key={i} className={`border-t border-border ${a.cancelled ? "line-through text-ink-muted" : ""}`}>
                        <td className="px-2 py-1 font-mono">{rcptNo}{a.cancelled ? " (CANCELLED)" : ""}</td>
                        <td className="px-2 py-1">{(a.received_at || a.date || "").slice(0, 10)}</td>
                        <td className="px-2 py-1 capitalize">{(a.mode || "").replace("_", " ")}</td>
                        <td className="px-2 py-1 font-mono">{a.reference || "—"}</td>
                        <td className="px-2 py-1 text-right">₹ {(a.amount || 0).toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
          {Array.isArray(inv.refunds) && inv.refunds.length > 0 && (
            <>
              <div className="mt-3 mb-1 font-semibold uppercase text-rose-700">Refunds issued</div>
              <table className="w-full border border-rose-200">
                <thead className="bg-rose-50 text-[10px]">
                  <tr>
                    <th className="px-2 py-1 text-left">Refund no.</th>
                    <th className="px-2 py-1 text-left">Date</th>
                    <th className="px-2 py-1 text-left">Mode</th>
                    <th className="px-2 py-1 text-left">Reason</th>
                    <th className="px-2 py-1 text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.refunds.map((r, i) => (
                    <tr key={i} className="border-t border-rose-100">
                      <td className="px-2 py-1 font-mono">{r.refund_no || `#${i + 1}`}</td>
                      <td className="px-2 py-1">{(r.refunded_at || "").slice(0, 10)}</td>
                      <td className="px-2 py-1 capitalize">{(r.mode || "cash").replace("_", " ")}</td>
                      <td className="px-2 py-1">{r.reason || "—"}</td>
                      <td className="px-2 py-1 text-right text-rose-700">− ₹ {(r.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
          {inv.notes && <div className="mt-3 text-ink-muted"><b>Notes:</b> {inv.notes}</div>}
        </div>

        <div className="space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-ink-muted">Gross</span><span>₹ {(inv.gross || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Discount</span><span>− ₹ {(inv.discount_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between"><span className="text-ink-muted">Tax</span><span>+ ₹ {(inv.tax_total || 0).toFixed(2)}</span></div>
          <div className="flex justify-between border-t border-border pt-1 font-heading text-base"><span>Total</span><span className="text-brand font-bold">₹ {(inv.total || 0).toFixed(2)}</span></div>
          {(() => {
            const advance = Number(inv.advance_paid || inv.amount_paid || 0);
            const refunded = Number(inv.refund_amount || 0);
            const netPaid = advance - refunded;
            const total = Number(inv.total || 0);
            const balance = Math.round((total - netPaid) * 100) / 100;
            return (
              <>
                <div className="flex justify-between text-xs"><span className="text-ink-muted">Advance paid</span><span className="text-emerald-700">− ₹ {advance.toFixed(2)}</span></div>
                {refunded > 0 && (
                  <div className="flex justify-between text-xs"><span className="text-ink-muted">Refunds issued</span><span className="text-rose-700">+ ₹ {refunded.toFixed(2)}</span></div>
                )}
                {refunded > 0 && (
                  <div className="flex justify-between text-xs font-semibold border-t border-dashed border-border pt-1"><span className="text-ink-muted">Net paid by patient</span><span className="text-emerald-700">₹ {netPaid.toFixed(2)}</span></div>
                )}
                <div className="flex justify-between border-t border-border pt-1 font-heading text-base">
                  <span>Balance due</span>
                  <span className={Math.abs(balance) < 0.01 ? "text-emerald-700 font-bold" : balance > 0 ? "text-rose-700 font-bold" : "text-amber-700 font-bold"}>₹ {balance.toFixed(2)}</span>
                </div>
                {Math.abs(balance) < 0.01 && refunded > 0 && (
                  <div className="text-right text-[10px] uppercase tracking-wider text-emerald-700">Fully settled (refund applied)</div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {/* Signature */}
      <div className="mt-10 flex justify-between text-[10px] text-ink-muted">
        <div>Patient / Attendant signature</div>
        <div>For {hospital?.name || BRAND.name} · Authorised signatory</div>
      </div>

      <div className="mt-8 border-t border-border pt-3 text-center text-[10px] text-ink-muted">{BRAND.footer}</div>
    </div>
  );
}
